import {
  authorizedFetch,
  API_ORIGIN,
  employerApplicantBrowseQueryParams,
  DEFAULT_EMPLOYER_APPLICANT_BROWSE_FILTERS,
} from '@yeshuman/sdk'
import type { EmployerApplicantBrowseFilters } from '@yeshuman/sdk'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { normalizeApplicantProfileFromApi } from './use-opportunity-applicants'

export interface EmployerRecentApplicantRow {
  id: string
  opportunity_id: string
  profile: ReturnType<typeof normalizeApplicantProfileFromApi>
  applied_at: string
  match_score: number | null
}

function browseApplicantsQueryKey(
  organisationSlug: string,
  filters: EmployerApplicantBrowseFilters,
  limit: number,
) {
  return [
    'managed-org-browse-applicants',
    organisationSlug,
    filters.textQuery,
    filters.opportunityId,
    filters.selectedSkills.join('\0'),
    limit,
  ] as const
}

async function fetchManagedOrgBrowseApplicants(
  organisationSlug: string,
  filters: EmployerApplicantBrowseFilters,
  limit: number,
): Promise<EmployerRecentApplicantRow[]> {
  const params = employerApplicantBrowseQueryParams(filters, limit)
  const response = await authorizedFetch(
    `${API_ORIGIN}/api/applications/managed-organisations/${encodeURIComponent(organisationSlug)}/browse-applicants/?${params.toString()}`,
  )
  if (!response.ok) {
    throw new Error(`Failed to load applicants (${response.status})`)
  }
  const data = (await response.json()) as { applicants?: unknown[] }
  const rows = data.applicants ?? []
  return rows.map((row): EmployerRecentApplicantRow => {
    const r = row as Record<string, unknown>
    const profileRaw = r.profile as Record<string, unknown> | undefined
    return {
      id: String(r.id ?? ''),
      opportunity_id: String(r.opportunity_id ?? ''),
      applied_at: typeof r.applied_at === 'string' ? r.applied_at : '',
      match_score:
        typeof r.match_score === 'number'
          ? r.match_score
          : r.match_score === null || r.match_score === undefined
            ? null
            : null,
      profile: normalizeApplicantProfileFromApi(profileRaw ?? {}),
    }
  })
}

export function useManagedOrgBrowseApplicants(
  organisationSlug: string | undefined,
  filters: EmployerApplicantBrowseFilters,
  limit = 50,
) {
  const query = useQuery({
    queryKey: organisationSlug?.trim()
      ? browseApplicantsQueryKey(organisationSlug, filters, limit)
      : ['managed-org-browse-applicants', 'disabled'],
    queryFn: () => fetchManagedOrgBrowseApplicants(organisationSlug!, filters, limit),
    enabled: !!organisationSlug?.trim(),
    placeholderData: keepPreviousData,
  })

  return {
    applicants: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  }
}

async function fetchManagedOrgRecentApplicants(
  organisationSlug: string,
  limit: number,
): Promise<EmployerRecentApplicantRow[]> {
  return fetchManagedOrgBrowseApplicants(
    organisationSlug,
    { ...DEFAULT_EMPLOYER_APPLICANT_BROWSE_FILTERS, selectedSkills: [] },
    limit,
  )
}

export function useManagedOrgRecentApplicants(organisationSlug: string | undefined, limit = 20) {
  const query = useQuery({
    queryKey: ['managed-org-recent-applicants', organisationSlug, limit],
    queryFn: () => fetchManagedOrgRecentApplicants(organisationSlug!, limit),
    enabled: !!organisationSlug?.trim(),
  })

  return {
    applicants: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}
